import { swivuRecentRecordSortFn } from "@/modern/utils";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { VIEW_TYPES } from "@/other/constants/OtherKeys";
import { SWIVU_STANDARD_ICONS } from "@/other/constants/IconKeys.jsx";
import type { Checklist } from "@/bryntum-lib/models/Checklist";
import { EntityFinderHighlight } from "@/modern/components/entity-finder/components/EntityFinderHighlight";
import { EntityFinderItemRow, entityFinderTintText } from "@/modern/components/entity-finder/components/EntityFinderItemRow";
import { EntityFinderMetaBadge } from "@/modern/components/entity-finder/components/EntityFinderMetaBadge";
import { renderEntityFinderConfigIcon } from "@/modern/components/entity-finder/components/renderEntityFinderConfigIcon";
import { estimateDefaultRowHeight, openEntityEditor } from "@/modern/components/entity-finder/constants/config/shared";
import type { EntityFinderConfigEntry } from "@/modern/components/entity-finder/types";
import {
  defaultEntityListSection,
  defaultMatchesScope,
  matchesSearch,
  passesArchiveFilter,
  searchModeActive,
} from "@/modern/components/entity-finder/utils";

function getChecklistProgress(checklist: Checklist): string | null {
  const items = checklist.checklistItems || [];
  if (!items.length) return null;
  const completed = items.filter((item: { isItemCheck?: boolean }) => item.isItemCheck).length;
  return `${completed} of ${items.length} done`;
}

function mapChecklist(checklist: Checklist) {
  return {
    id: `checklist-${checklist.id}`,
    type: "checklist" as const,
    label: checklist.title || checklist.name || "N/A",
    subtitle: checklist.name || undefined,
    record: checklist,
  };
}

export const checklistConfig: EntityFinderConfigEntry<"checklist"> = {
  type: "checklist",
  label: "Checklist",
  plural: "Checklists",
  icon: <i className={SWIVU_STANDARD_ICONS[VIEW_TYPES.checklists]} aria-hidden />,
  tint: "bg-chart-2/15 text-chart-2",
  listOrder: 70,
  showInFilter: true,
  isActive: searchModeActive,
  matchesScope: (ctx) => defaultMatchesScope(ctx, "checklist"),
  getData: (ctx) => {
    const searchLower = ctx.debouncedQuery.toLowerCase();
    const records = swivuRecentRecordSortFn(
      ctx.project.checklistStore.query(
        (checklist: Checklist) =>
          passesArchiveFilter(checklist.archived, ctx.isShowArchive) &&
          !checklist.isLinked &&
          (matchesSearch(checklist.name, searchLower) || matchesSearch(checklist.title, searchLower)),
        true
      ) as unknown as Checklist[],
      {
        currentBoardUserId: ctx.currentBoardUserId,
        nameField: "title",
        getUpdatedByField: (r) => (r as Checklist).updater,
        getUpdatedAtField: (r) => (r as Checklist).createdAt,
      }
    );
    return records.map(mapChecklist);
  },
  getRecentItem: (ctx) => {
    const records = swivuRecentRecordSortFn(
      ctx.project.checklistStore.query(
        (c: Checklist) => passesArchiveFilter(c.archived, ctx.isShowArchive) && !c.isLinked,
        true
      ) as unknown as Checklist[],
      {
        currentBoardUserId: ctx.currentBoardUserId,
        nameField: "title",
        getUpdatedByField: (r) => (r as Checklist).updater,
        getUpdatedAtField: (r) => (r as Checklist).createdAt,
      }
    ).slice(0, 1);
    return records[0] ? mapChecklist(records[0]) : null;
  },
  buildListSection: (items, _ctx) => defaultEntityListSection(checklistConfig, items),
  estimateRowHeight: estimateDefaultRowHeight,
  getMetadata: (item) => {
    const progress = getChecklistProgress(item.record as Checklist);
    return progress ? { text: progress, variant: "status" } : null;
  },
  itemRenderer: ({ item, query, ctx, metadata, entry }) => {
    const badge = metadata ?? checklistConfig.getMetadata?.(item, ctx);

    return (
      <EntityFinderItemRow
        icon={renderEntityFinderConfigIcon(entry.icon, 14, "text-sm")}
        iconClassName={entityFinderTintText(checklistConfig.tint)}
        title={<EntityFinderHighlight text={item.label} query={query} />}
        subtitle={
          item.subtitle ? (
            <EntityFinderHighlight text={item.subtitle} query={query} tone="muted" />
          ) : undefined
        }
        trailing={
          badge ? (
            <EntityFinderMetaBadge variant="status" title={badge.text}>
              {badge.text}
            </EntityFinderMetaBadge>
          ) : undefined
        }
      />
    );
  },
  onSelect: (item, ctx) => {
    const checklist = item.record as Checklist;
    openEntityEditor(
      ctx,
      EDITOR_KEYS.checklistEditor,
      null,
      { entity: checklist.entity, checklistId: checklist.id },
      { by: "recordType", useExisting: true }
    );
  },
};
